/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2020 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";

import {
  MenuItem,
  InputLabel,
  Grid,
  CircularProgress,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import AlertsIcon from "../common/AlertsIcon";

import StyledFormControl from "../common/StyledFormControl";
import StyledSelect from "../common/StyledSelect";
import StyledTextField from "../common/StyledTextField";
import BaseButton from "../common/BaseButton";
import types from "../../helpers/types";
import Toggle from "../common/Toggle";
import {
  isAdministrator,
  isShipperManager,
} from "../../helpers/goTrackUserUtils";

import { fetchCarriers } from "../../slices/carrierManagementSlice";
import { putCarrierUpdate } from "../../api/api";
import { searchCarriers } from "../../slices/carrierManagementSlice";

function CarrierUpdateForm({
  setSuccessMessage,
  setUnsavedChanges,
  openedRow,
}) {
  const dispatch = useDispatch();
  const theme = useTheme();

  const user = useSelector((state) => state.goTrackUser.goTrackUser);
  const displayClaimFeatureToggle =
    (isShipperManager(user.roles) || isAdministrator(user.roles));

  const [carrierUpdateRequestStatus, setCarrierUpdateRequestStatus] = useState(
    types.requestStatuses.initial
  );

  // carrier update form inputs
  const [carrierTier, setCarrierTier] = useState(openedRow.tierName);
  const [contactName, setContactName] = useState(openedRow.contactFullName);

  const [phoneNumber1, setPhoneNumber1] = useState(openedRow.phone1);
  const [phoneNumber2, setPhoneNumber2] = useState(openedRow.phone2);
  const [phoneNumber3, setPhoneNumber3] = useState(openedRow.phone3);
  const [claimsEnabled, setClaimsEnabled] = useState(openedRow.claimsEnabled);

  const [email1, setEmail1] = useState(openedRow.email);
  const [email2, setEmail2] = useState(openedRow.email2);
  const [email3, setEmail3] = useState(openedRow.email3);

  // redux state
  const isFetchingCarriers = useSelector(
    (state) => state.carrierManagementCarriers.isFetching
  );

  useEffect(() => {
    setContactName(openedRow.contactFullName);
    setCarrierTier(openedRow.tierName);
    setPhoneNumber1(openedRow.phone1);
    setPhoneNumber2(openedRow.phone2);
    setPhoneNumber3(openedRow.phone3);
    setClaimsEnabled(openedRow.claimsEnabled);
    setEmail1(openedRow.email);
    setEmail2(openedRow.email2);
    setEmail3(openedRow.email3);
  }, [openedRow]);

  const handleTierUpdateSelectChange = (event) => {
    const tier = Number(event.target.value);
    setCarrierTier(tier);
    setUnsavedChanges("carrierForm", true);
  };

  const handleClaimsToggle = () => {
    setClaimsEnabled(!claimsEnabled);
    setUnsavedChanges("carrierForm", true);
  };

  const handleContactNameChange = (event) => {
    const contactName = event.target.value;
    setContactName(contactName);
    setUnsavedChanges("carrierForm", true);
  };

  const handleEmailChange = (event, emailNumber) => {
    const email = event.target.value;
    if (emailNumber === 1) {
      setEmail1(email);
    } else if (emailNumber === 2) {
      setEmail2(email);
    } else {
      setEmail3(email);
    }
    setUnsavedChanges("carrierForm", true);
  };

  const handlePhoneNumberChange = (event, inputNumber) => {
    const phoneNumber = event.target.value;
    if (inputNumber === 1) {
      setPhoneNumber1(phoneNumber);
    } else if (inputNumber === 2) {
      setPhoneNumber2(phoneNumber);
    } else {
      setPhoneNumber3(phoneNumber);
    }
    setUnsavedChanges("carrierForm", true);
  };

  const handleSaveButtonClick = async () => {
    const updates = {
      contactFullName: contactName,
      carrierEmail: email1,
      email2: email2,
      email3: email3,
      carrierPhone1: phoneNumber1,
      carrierPhone2: phoneNumber2,
      carrierPhone3: phoneNumber3,
      carrierTier: carrierTier,
      organizationId: openedRow.organizationsId,
      claimsEnabled: claimsEnabled,
    };
    setCarrierUpdateRequestStatus(types.requestStatuses.inProgress);
    try {
      await putCarrierUpdate(openedRow.carrierId, updates);
      setCarrierUpdateRequestStatus(types.requestStatuses.success);
      setSuccessMessage("Carrier updated.");
      dispatch(fetchCarriers());
      setUnsavedChanges("carrierForm", false);
      dispatch(searchCarriers());
    } catch {
      setCarrierUpdateRequestStatus(types.requestStatuses.failure);
    }
  };

  const carrierUpdateInProgress =
    carrierUpdateRequestStatus === types.requestStatuses.inProgress &&
    isFetchingCarriers;

  return (
    <Grid item xs={12}>
      <Grid container spacing={2}>
        {displayClaimFeatureToggle && (
          <Grid item xs={7}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: "80%",
              }}
            >
              <span style={{ whiteSpace: "nowrap", marginRight: "4px" }}>
                Claim feature
              </span>
              <Toggle checked={!claimsEnabled} onChange={handleClaimsToggle} />
              <span style={{ whiteSpace: "nowrap", marginLeft: "4px" }}>
                Call to claim
              </span>
            </div>
          </Grid>
        )}
        <Grid item xs={6}>
          <StyledTextField
            label="Contact"
            variant="outlined"
            value={contactName ?? ""}
            onChange={handleContactNameChange}
            fullWidth={true}
            inputProps={{
              "data-testid": "carrier-update-contact-text-field",
              maxLength: 256,
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <StyledFormControl variant="outlined" fullWidth={true}>
            <InputLabel id="tier-type" shrink={true}>
              Tier
            </InputLabel>
            <StyledSelect
              value={carrierTier}
              label="Tier"
              labelId="tier-type"
              onChange={handleTierUpdateSelectChange}
              data-testid="carrier-update-tier-select"
              inputProps={{
                "data-testid": "carrier-update-tier-select-input",
              }}
            >
              <MenuItem value={types.tiers.tier1}>Tier 1</MenuItem>
              <MenuItem value={types.tiers.tier2}>Tier 2</MenuItem>
              <MenuItem value={types.tiers.tier3}>Tier 3</MenuItem>
              <MenuItem value={types.tiers.doNotUse}>Do Not Use</MenuItem>
            </StyledSelect>
          </StyledFormControl>
        </Grid>
        <Grid item xs={6}>
          <StyledTextField
            label="Phone #"
            variant="outlined"
            value={phoneNumber1 ?? ""}
            onChange={(phoneNumber) => handlePhoneNumberChange(phoneNumber, 1)}
            fullWidth={true}
            inputProps={{
              "data-testid": "carrier-update-phone-number-text-field",
              maxLength: 15,
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <StyledTextField
            label="Email"
            variant="outlined"
            value={email1 ?? ""}
            onChange={(email) => {
              handleEmailChange(email, 1);
            }}
            fullWidth={true}
            inputProps={{
              "data-testid": "carrier-update-email-text-field",
              maxLength: 320,
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <StyledTextField
            label="Phone #"
            variant="outlined"
            value={phoneNumber2 ?? ""}
            onChange={(phoneNumber) => handlePhoneNumberChange(phoneNumber, 2)}
            fullWidth={true}
            inputProps={{
              "data-testid": "carrier-update-phone-number2-text-field",
              maxLength: 15,
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <StyledTextField
            label="Email"
            variant="outlined"
            value={email2 ?? ""}
            onChange={(email) => {
              handleEmailChange(email, 2);
            }}
            fullWidth={true}
            inputProps={{
              "data-testid": "carrier-update-email2-text-field",
              maxLength: 100,
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <StyledTextField
            label="Phone #"
            variant="outlined"
            value={phoneNumber3 ?? ""}
            onChange={(phoneNumber) => handlePhoneNumberChange(phoneNumber, 3)}
            fullWidth={true}
            inputProps={{
              "data-testid": "carrier-update-phone-number3-text-field",
              maxLength: 15,
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <StyledTextField
            label="Email"
            variant="outlined"
            value={email3 ?? ""}
            onChange={(email) => {
              handleEmailChange(email, 3);
            }}
            fullWidth={true}
            inputProps={{
              "data-testid": "carrier-update-email3-text-field",
              maxLength: 100,
            }}
          />
        </Grid>
        <Grid item xs={6}>
          Associated users
          <div style={{ padding: "16px" }}>
            {openedRow.associatedUsersCount}

            {openedRow.associatedUsersCount === 0 && (
              <Tooltip
                title={
                  "With no associated user(s), this carrier will not see anything in RouteConnect™ Available Routes or RouteConnect™ Tracking! Go to User Management to invite a new user."
                }
              >
                <AlertsIcon
                  color={theme.palette.error.main}
                  style={{ verticalAlign: "middle", marginLeft: "10px" }}
                />
              </Tooltip>
            )}
          </div>
        </Grid>
        <Grid item xs={6}>
          <BaseButton
            onClick={handleSaveButtonClick}
            fullWidth={true}
            disabled={carrierUpdateInProgress}
            startIcon={
              carrierUpdateInProgress ? <CircularProgress size={15} /> : null
            }
            data-testid="carrier-update-submit-button"
          >
            Save Changes
          </BaseButton>
        </Grid>
      </Grid>
    </Grid>
  );
}

CarrierUpdateForm.propTypes = {
  setSuccessMessage: PropTypes.func.isRequired,
  setUnsavedChanges: PropTypes.func.isRequired,
  openedRow: PropTypes.object.isRequired,
};

export default CarrierUpdateForm;
